import {
    EChartsOption,
} from '@/components/ECharts';
import { colors } from '@/components/ECharts/colors';
import { BarSeriesOption, LineSeriesOption } from 'echarts';
import { currencyFormat } from '../../helpers/currencyFormat';

export type ChartOptions = EChartsOption<BarSeriesOption | LineSeriesOption>;

export const getGraphData = ({
    seriesData,
    kpiSeriesData,
    xAxisData,
    period,
}: {
    xAxisData: string[];
    seriesData: {
        [key: string]: {
            data: { [key: string]: number };
            color: string;
        };
    };
    kpiSeriesData: { [key: string]: number };
    period: string | undefined;
}): ChartOptions => {
    const dates = xAxisData.filter((date, index) => {
        return xAxisData.indexOf(date) === index;
    });

    const kpiSeries = {
        name: 'KPI',
        type: 'line',
        yAxisIndex: 1,
        color: colors[2],
        itemStyle: {
            borderWidth: '4',
        },
        lineStyle: {
            width: 3,
        },
        symbol: 'circle',
        showAllSymbol: true,
        sampling: 'lttb',
        data: Object.entries(kpiSeriesData).map(([name, value]) => ({
            name,
            value,
        })),
        smooth: false,
        symbolSize: 7,
    } as unknown as ChartOptions['series'][0];

    const seriesEntries = Object.entries(seriesData);
    const series: ChartOptions['series'] = seriesEntries.map(
        ([key, { data, color }], index) => {
            return {
                name: key,
                type: 'bar',
                stack: 'Total',
                symbolSize: 2,
                showSymbol: false,
                showAllSymbol: false,
                triggerLineEvent: true,
                color: color,
                barMaxWidth: 50,
                large: true,
                largeThreshold: 200,
                progressive: 2000,
                progressiveThreshold: 3000,
                itemStyle: {
                    borderRadius:
                        index === seriesEntries.length - 1 ? [5, 5, 0, 0] : 0,
                },
                data: Object.entries(data).map(([name, value]) => ({
                    name,
                    value,
                })),
            } as unknown as ChartOptions['series'][0];
        },
    );
    series.push(kpiSeries);

    return {
        animation: true,
        tooltip: {
            show: false,
            trigger: 'axis',
            triggerOn: 'mousemove|click',
            backgroundColor: 'transparent',
            borderWidth: 0,
            padding: 0,
            type: 'item',
            confine: true,
            extraCssText: 'max-width: 600px; white-space: pre-wrap;',
            formatter: '',
        },
        legend: {
            show: false,
        },
        dataZoom: [
            {
                type: 'inside',
                xAxisIndex: [0],
                zoomOnMouseWheel: 'ctrl',
                moveOnMouseWheel: false,
                moveOnMouseMove: true,
                throttle: 50,
            },
            {
                id: 'dataZoomX',
                type: 'slider',
                xAxisIndex: [0],
                bottom: 10,
                labelFormatter: '',
                textStyle: { show: false },
                throttle: 50,
            },
        ],
        grid: {
            right: '2%',
            left: '7%',
            bottom: '15%',
        },
        xAxis: [
            {
                type: 'category',
                boundaryGap: true,
                show: true,
                axisLine: {
                    show: false,
                },
                data: dates,
                axisLabel: {
                    rotate: 90,
                    margin: period === 'yearly' ? 30 : 20,
                },
            },
        ],
        yAxis: [
            {
                type: 'value',
                axisLabel: {
                    formatter: (value: number) =>
                        currencyFormat.format(Number(value)),
                },
                axisLine: {
                    show: false,
                },
                splitLine: {
                    show: true,
                    lineStyle: {
                        opacity: 0.1,
                    },
                },
            },
            {
                type: 'value',
                show: false,
            },
        ],
        series: series,
    };
};
